import React, { useState, useEffect, useCallback } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { ClipboardCheck, Clock, AlertTriangle, Flame, BookOpen } from 'lucide-react';
import ApprovalQueue from '../components/approvals/ApprovalQueue';
import AmendmentRequests from '../components/approvals/AmendmentRequests';
import ExceptionsTab from '../components/approvals/ExceptionsTab';
import UncertifiedList from '../components/approvals/UncertifiedList';

const API = `${window.__BACKEND_URL__}/api`;

const TABS = [
  { key: 'queue', label: 'Pending Approvals' },
  { key: 'uncertified', label: 'Not Certified' },
  { key: 'amendments', label: 'Amendment Requests' },
  { key: 'exceptions', label: 'Exceptions' },
];

export default function Approvals() {
  const { user, token } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTab = TABS.some((t) => t.key === searchParams.get('tab')) ? searchParams.get('tab') : 'queue';
  const [tab, setTab] = useState(initialTab);
  const [summary, setSummary] = useState(null);

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/payroll/readiness`,
        { headers: { Authorization: `Bearer ${token}` } });
      setSummary(data);
    } catch (e) { /* header just won't show */ }
  }, [token]);

  useEffect(() => { if (token) loadSummary(); }, [token, loadSummary]);

  if (user && !['admin', 'supervisor'].includes(user.role) && !user.is_super_user) {
    return <Navigate to="/home" replace />;
  }

  const isAdmin = user?.role === 'admin' || user?.is_super_user;
  const pct = summary?.readiness_pct ?? 0;
  const pctColor = pct >= 90 ? '#00C896' : pct >= 60 ? '#FFA000' : '#EF4444';

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="approvals-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-white">Approvals</h1>
          <p className="text-sm mt-0.5" style={{ color: '#A0AEC0' }}>
            Review, approve, and return your team's timesheets before payroll.
          </p>
        </div>
        <a href="/user-manual?tab=payroll"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium"
          style={{ background: '#1A2433', color: '#93C5FD', border: '1px solid #2D3748' }}
          data-testid="approvals-guide-link">
          <BookOpen className="w-4 h-4" /> Timekeeping guide
        </a>
      </div>

      {/* Summary bar */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="approvals-summary-bar">
          <div className="ht-card p-4">
            <div className="flex items-center gap-2 mb-1">
              <ClipboardCheck className="w-4 h-4" style={{ color: pctColor }} />
              <p className="text-[11px] uppercase tracking-wide" style={{ color: '#64748B' }}>Team Payroll Readiness</p>
            </div>
            <p className="text-2xl font-bold" style={{ color: pctColor }} data-testid="readiness-pct">{pct}%</p>
            <p className="text-[11px] mt-0.5" style={{ color: '#4A5568' }}>
              {summary.ready_entries}/{summary.total_entries} entries · {summary.period_start} → {summary.period_end}
            </p>
          </div>
          <div className="ht-card p-4">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4" style={{ color: '#60A5FA' }} />
              <p className="text-[11px] uppercase tracking-wide" style={{ color: '#64748B' }}>Entries Pending</p>
            </div>
            <p className="text-2xl font-bold text-white" data-testid="pending-count">{summary.pending_entries}</p>
          </div>
          <div className="ht-card p-4">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4" style={{ color: '#FBBF24' }} />
              <p className="text-[11px] uppercase tracking-wide" style={{ color: '#64748B' }}>Exceptions Open</p>
            </div>
            <p className="text-2xl font-bold text-white" data-testid="exceptions-open-count">{summary.exceptions_open}</p>
          </div>
          <div className="ht-card p-4">
            <div className="flex items-center gap-2 mb-1">
              <Flame className="w-4 h-4" style={{ color: '#FB923C' }} />
              <p className="text-[11px] uppercase tracking-wide" style={{ color: '#64748B' }}>OT Alerts</p>
            </div>
            <p className="text-2xl font-bold text-white" data-testid="ot-alerts-count">{summary.ot_alerts}</p>
          </div>
        </div>
      )}

      {/* Sub-tabs */}
      <div className="flex flex-wrap gap-2" data-testid="approvals-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className="px-3.5 py-2 rounded-xl text-sm font-medium transition-colors"
            style={{
              background: tab === t.key ? '#1E88E5' : '#1A2433',
              color: tab === t.key ? '#fff' : '#A0AEC0',
              border: `1px solid ${tab === t.key ? '#1E88E5' : '#2D3748'}`,
            }}
            data-testid={`approvals-tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'queue' && <ApprovalQueue token={token} onChanged={loadSummary} />}
      {tab === 'uncertified' && <UncertifiedList token={token} onChanged={loadSummary} />}
      {tab === 'amendments' && <AmendmentRequests token={token} onChanged={loadSummary} />}
      {tab === 'exceptions' && <ExceptionsTab token={token} isAdmin={isAdmin} onChanged={loadSummary} />}
    </div>
  );
}
